import { useEffect, useMemo, useState } from "react";
import { Flex, Spinner, Text } from "@chakra-ui/react";
import {
  Select,
  OptionBase,
  GroupBase,
  chakraComponents,
  type Props as SelectProps,
} from "chakra-react-select";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import {
  useDeviationCategoryOptions,
  type DeviationCategoryValueField,
} from "hooks/useDeviationCategoryOptions";

export interface DeviationReasonOption extends OptionBase {
  label: string;
  value: string;
}

export type DeviationReasonSelectProps = {
  value: string;
  /** Etiqueta conocida (p. ej. desde GET session) sin cargar el catálogo completo. */
  selectedLabel?: string | null;
  onChange: (value: string, label?: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  /** Tamaño compacto para celdas de tabla (`sm`; chakra-react-select no expone `xs`). */
  size?: "sm" | "md";
  /**
   * `name`: texto del motivo (columna «Actual», API coverage).
   * `id`: PK del catálogo `deviation_note` (columnas por mes/semana, Agente 8).
   */
  optionValue?: "id" | "name";
  /** Abre el menú al montar (útil con lazy mount). */
  defaultMenuIsOpen?: boolean;
  /** Se invoca al cerrar el menú sin confirmar selección. */
  onDismiss?: () => void;
};

const SEARCH_DEBOUNCE_MS = 500;

function humanizeLoadError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return `No pudimos cargar los motivos: ${error.message}`;
  }
  return "No pudimos cargar los motivos. Revisá tu conexión e intentá de nuevo.";
}

export function DeviationReasonSelect(props: DeviationReasonSelectProps) {
  const {
    value,
    selectedLabel,
    onChange,
    isDisabled = false,
    placeholder = "Motivo…",
    size = "sm",
    optionValue = "name",
    defaultMenuIsOpen = false,
    onDismiss,
  } = props;

  const [menuOpen, setMenuOpen] = useState(defaultMenuIsOpen);
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebouncedValue(inputValue, SEARCH_DEBOUNCE_MS);

  const {
    options,
    allCategories,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
  } = useDeviationCategoryOptions({
    search: debouncedSearch,
    enabled: menuOpen,
    valueField: optionValue,
  });

  /** Sin búsqueda server-side: precarga páginas si el filtro local no devuelve coincidencias. */
  useEffect(() => {
    if (
      !menuOpen ||
      !debouncedSearch.trim() ||
      options.length > 0 ||
      !hasNextPage ||
      isFetchingNextPage
    ) {
      return;
    }
    void fetchNextPage();
  }, [
    menuOpen,
    debouncedSearch,
    options.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const selectedOption = useMemo((): DeviationReasonOption | null => {
    if (!value) {
      return null;
    }
    if (selectedLabel?.trim()) {
      return { label: selectedLabel, value };
    }
    const match = options.find((opt) => opt.value === value);
    if (match) {
      return { label: match.label, value: match.value };
    }
    if (optionValue === "id") {
      const byId = allCategories.find((cat) => cat.id === value);
      if (byId) {
        return { label: byId.name, value: byId.id };
      }
    }
    return { label: value, value };
  }, [value, selectedLabel, options, optionValue, allCategories]);

  const noOptionsMessage = (): string => {
    if (isError) {
      return "Error al cargar motivos";
    }
    if (isLoading) {
      return "Cargando motivos…";
    }
    if (options.length === 0 && hasNextPage) {
      return "Sin coincidencias. Desplazá hacia abajo para cargar más.";
    }
    if (debouncedSearch.trim()) {
      return "No hay motivos que coincidan con tu búsqueda";
    }
    return "Aún no hay categorías de desviación registradas";
  };

  const handleMenuScrollToBottom = () => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const selectProps: SelectProps<
    DeviationReasonOption,
    false,
    GroupBase<DeviationReasonOption>
  > = {
    size,
    options,
    value: selectedOption,
    inputValue,
    placeholder: isDisabled ? "—" : placeholder,
    isSearchable: !isDisabled,
    isClearable: !isDisabled,
    isDisabled,
    filterOption: () => true,
    menuIsOpen: menuOpen,
    isLoading: isLoading || isFetchingNextPage,
    loadingMessage: () => "Cargando motivos…",
    noOptionsMessage,
    onMenuOpen: () => setMenuOpen(true),
    onMenuClose: () => {
      setMenuOpen(false);
      setInputValue("");
      onDismiss?.();
    },
    onInputChange: (next, meta) => {
      if (meta.action === "input-change") {
        setInputValue(next);
      }
    },
    onChange: (opt) => {
      onChange(opt?.value ?? "", opt?.label);
    },
    onMenuScrollToBottom: handleMenuScrollToBottom,
    menuPlacement: "auto",
    menuPosition: "fixed",
    menuPortalTarget:
      typeof document !== "undefined" ? document.body : undefined,
    maxMenuHeight: 180,
    styles: {
      menuPortal: (base) => ({
        ...base,
        zIndex: 1600,
      }),
    },
    chakraStyles: {
      container: (provided) => ({
        ...provided,
        width: "100%",
        minWidth: 0,
      }),
      control: (provided) => ({
        ...provided,
        minHeight: size === "sm" ? "28px" : provided.minHeight,
        fontSize: size === "sm" ? "10px" : provided.fontSize,
      }),
    },
    components: {
      MenuList: (menuProps) => (
        <chakraComponents.MenuList {...menuProps}>
          {menuProps.children}
          {isFetchingNextPage ? (
            <Flex justify="center" py={2}>
              <Spinner size="sm" />
            </Flex>
          ) : null}
        </chakraComponents.MenuList>
      ),
    },
  };

  return (
    <Flex direction="column" gap={1} w="100%" minW={0}>
      <Select<DeviationReasonOption, false, GroupBase<DeviationReasonOption>>
        {...selectProps}
      />
      {isError ? (
        <Text fontSize="9px" color="red.400" lineHeight="short">
          {humanizeLoadError(error)}
        </Text>
      ) : null}
    </Flex>
  );
}
